import React, { useState } from 'react';
import { Calculator as CalcIcon } from 'lucide-react';
import { Currency } from '../types/calculator';
import { ComparisonResults } from './ComparisonResults';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { calculateFees, PROVIDERS } from '../utils/feeCalculator';
import { trackCompareProviders } from '../utils/analytics';
export function Calculator() {
  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<Currency>('EUR');
  const [showResults, setShowResults] = useState(false);
  const {
    language
  } = useLanguage();
  const t = translations[language].calculator;
  const handleCompare = () => {
    if (amount && parseFloat(amount) > 0) {
      setShowResults(true);
      // Calculate results for tracking
      const amountNum = parseFloat(amount);
      const djamoBreakdown = calculateFees(amountNum, currency, 'djamo');
      const waveBreakdown = calculateFees(amountNum, currency, 'wave');
      const cheapestProvider = djamoBreakdown.totalCost < waveBreakdown.totalCost ? 'Djamo' : 'Wave';
      const savings = Math.abs(djamoBreakdown.totalCost - waveBreakdown.totalCost);
      // Track the comparison event
      trackCompareProviders({
        amount: amountNum,
        currency,
        djamoTotalCost: djamoBreakdown.totalCost,
        waveTotalCost: waveBreakdown.totalCost,
        cheapestProvider,
        savings
      });
    }
  };
  const handleAmountChange = (value: string) => {
    // Only allow numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
      setShowResults(false);
    }
  };
  return <section id="calculator" className="py-16 sm:py-24 bg-white" data-id="element-3">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4">
        <div className="text-center mb-12" data-id="element-5">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 rounded-2xl mb-4" data-id="element-6">
            <CalcIcon className="w-8 h-8 text-white" data-id="element-7" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900" data-id="element-8">
            {t.title}
          </h2>
          <p className="mt-4 text-lg text-gray-600" data-id="element-9">{t.subtitle}</p>
        </div>

        <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200" data-id="element-10">
          <div className="grid sm:grid-cols-2 gap-6 mb-6" data-id="element-11">
            <div data-id="element-12">
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-13">
                {t.amountLabel}
              </label>
              <input id="amount" type="text" inputMode="decimal" value={amount} onChange={e => handleAmountChange(e.target.value)} placeholder="50" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none text-lg" data-id="element-14" />
            </div>

            <div data-id="element-15">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-16">
                {t.currencyLabel}
              </label>
              <select id="currency" value={currency} onChange={e => {
              setCurrency(e.target.value as Currency);
              setShowResults(false);
            }} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none text-lg bg-white" data-id="element-17">
                <option value="EUR" data-id="element-18">{t.currencyEur}</option>
                <option value="USD" data-id="element-19">{t.currencyUsd}</option>
                <option value="XOF" data-id="element-20">{t.currencyXof}</option>
              </select>
            </div>
          </div>

          <button onClick={handleCompare} disabled={!amount || parseFloat(amount) <= 0} className="w-full bg-gray-900 text-white px-6 py-4 rounded-lg text-lg font-medium hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-21">
            {t.compareButton}
          </button>
        </div>

        {showResults && amount && <ComparisonResults amount={parseFloat(amount)} currency={currency} data-id="element-22" />}
      </div>
    </section>;
}